import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FiFilter } from 'react-icons/fi';

const popoverCardStyle = {
  position: 'fixed',
  zIndex: 9999,
  minWidth: 300,
  maxWidth: 380,
  background: '#ffffff',
  border: '1px solid #E2E8F0',
  borderRadius: 12,
  boxShadow: '0 20px 40px rgba(2, 6, 23, 0.14)',
  padding: 12
};

const inputStyle = {
  width: '100%',
  height: 36,
  borderRadius: 10,
  border: '1px solid #CBD5E1',
  background: '#F8FAFC',
  color: '#0F172A',
  padding: '8px 10px',
  fontSize: 13,
  outline: 'none'
};

function safeText(value, fallback) {
  const v = String(value || '').trim();
  if (!v || v.includes('.')) return fallback;
  return v;
}

function MultiSelectFilter({ label, options, value, onChange }) {
  const [search, setSearch] = useState('');
  const selected = Array.isArray(value) ? value.map((v) => String(v)) : [];
  const all = (options || []).map((o) => String(o.value));
  const filtered = useMemo(() => {
    const q = String(search || '').trim().toLowerCase();
    if (!q) return options || [];
    return (options || []).filter((o) => String(o.label || '').toLowerCase().includes(q));
  }, [options, search]);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <div style={{ fontSize: 12, color: '#64748B', fontWeight: 700 }}>{label}</div>
        <div style={{ fontSize: 12, color: '#0F172A', fontWeight: 600 }}>{selected.length === 0 ? 'All' : `${selected.length} selected`}</div>
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
        <button type="button" className="btn btn-outline btn-sm" onClick={() => onChange(all)}>Select All</button>
        <button type="button" className="btn btn-outline btn-sm" onClick={() => onChange([])}>Clear</button>
      </div>
      <input
        type="text"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder={`Search ${label.toLowerCase()}`}
        style={{ ...inputStyle, marginBottom: 8 }}
      />
      <div style={{ maxHeight: 240, overflowY: 'auto', display: 'grid', gap: 6, paddingRight: 2 }}>
        {filtered.map((opt) => {
          const v = String(opt.value);
          const checked = selected.includes(v);
          return (
            <label
              key={v}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                fontSize: 13,
                background: checked ? '#EFF6FF' : '#FFFFFF',
                border: `1px solid ${checked ? '#BFDBFE' : '#E2E8F0'}`,
                borderRadius: 8,
                padding: '6px 8px'
              }}
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={(e) => {
                  if (e.target.checked) onChange(Array.from(new Set([...selected, v])));
                  else onChange(selected.filter((x) => x !== v));
                }}
              />
              <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{opt.label}</span>
            </label>
          );
        })}
        {!filtered.length && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>No matches found</div>}
      </div>
    </div>
  );
}

export function UnifiedTableToolbar({
  draft,
  onDraftChange,
  onReset,
  hasActiveFilters,
  searchPlaceholder = 'Search anything',
  extraFilters = null
}) {
  return (
    <div className="table-toolbar">
      <div className="toolbar-left" style={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'center', gap: 10, overflowX: 'auto' }}>
        <div className="table-search" style={{ minWidth: 260 }}>
          <input
            type="search"
            placeholder={searchPlaceholder}
            value={draft.globalSearch || ''}
            onChange={(e) => onDraftChange((prev) => ({ ...prev, globalSearch: e.target.value }))}
            autoComplete="off"
            style={{ height: 38, borderRadius: 10 }}
          />
        </div>
        {extraFilters}
        <button type="button" className="btn btn-outline btn-sm" disabled={!hasActiveFilters} onClick={onReset}>Reset</button>
      </div>
    </div>
  );
}

function ColumnFilterPopover({ column, value, onValueChange }) {
  const key = column.key;
  if (column.type === 'multi') {
    return (
      <MultiSelectFilter
        label={safeText(column.label, key)}
        options={column.options || []}
        value={value || []}
        onChange={onValueChange}
      />
    );
  }
  if (column.type === 'date') {
    return (
      <div style={{ display: 'grid', gap: 6, minWidth: 200 }}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 2 }}>
          <button
            type="button"
            className="btn btn-outline btn-sm"
            onClick={() => onValueChange({ from: '', to: '' })}
          >
            Clear
          </button>
        </div>
        <label style={{ fontSize: 12, color: '#64748B', fontWeight: 600 }}>From</label>
        <input
          type="date"
          style={inputStyle}
          value={value?.from || ''}
          onChange={(e) => onValueChange({ ...(value || {}), from: e.target.value })}
        />
        <label style={{ fontSize: 12, color: '#64748B', fontWeight: 600, marginTop: 2 }}>To</label>
        <input
          type="date"
          style={inputStyle}
          value={value?.to || ''}
          onChange={(e) => onValueChange({ ...(value || {}), to: e.target.value })}
        />
      </div>
    );
  }
  return (
    <input
      type="text"
      placeholder={`Filter ${safeText(column.label, key)}`}
      value={value || ''}
      onChange={(e) => onValueChange(e.target.value)}
      style={inputStyle}
    />
  );
}

function isActiveValue(type, value) {
  if (type === 'multi') return Array.isArray(value) && value.length > 0;
  if (type === 'date') return Boolean(value?.from || value?.to);
  return Boolean(String(value || '').trim());
}

export function UnifiedFilterHeaderCell({ column, draftColumns, onChange }) {
  const key = column.key;
  const value = draftColumns?.[key];
  const active = isActiveValue(column.type, value);
  const detailsRef = useRef(null);
  const triggerRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [popoverPos, setPopoverPos] = useState({ top: 0, left: 0 });

  const updatePopoverPosition = () => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const width = 340;
    const minLeft = 8;
    const maxLeft = Math.max(8, window.innerWidth - width - 8);
    const left = Math.min(maxLeft, Math.max(minLeft, rect.right - width));
    setPopoverPos({
      top: rect.bottom + 10,
      left
    });
  };

  useEffect(() => {
    const handleOutsideClick = (event) => {
      const details = detailsRef.current;
      if (!details || !details.open) return;
      if (!details.contains(event.target)) {
        details.open = false;
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;
    updatePopoverPosition();
    const reposition = () => updatePopoverPosition();
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    return () => {
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [isOpen]);

  return (
    <th>
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, position: 'relative' }}>
        <span>{column.label}</span>
        <details
          ref={detailsRef}
          style={{ position: 'relative' }}
          onToggle={(e) => {
            const open = e.currentTarget.open;
            setIsOpen(open);
            if (open) updatePopoverPosition();
          }}
        >
          <summary
            ref={triggerRef}
            style={{
              listStyle: 'none',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 22,
              height: 22,
              borderRadius: 6,
              border: `1px solid ${active ? '#60A5FA' : '#CBD5E1'}`,
              background: active ? '#EFF6FF' : '#FFFFFF',
              color: active ? '#2563EB' : '#64748B'
            }}
            title={`Filter ${safeText(column.label, key)}`}
          >
            <FiFilter size={14} />
          </summary>
          {isOpen && (
            <div style={{ ...popoverCardStyle, top: popoverPos.top, left: popoverPos.left }}>
              <ColumnFilterPopover
                column={column}
                value={value}
                onValueChange={(v) => onChange((prev) => ({ ...prev, [key]: v }))}
              />
            </div>
          )}
        </details>
      </div>
    </th>
  );
}

